import React from 'react';
import { Calendar, CheckCircle, AlertCircle, Briefcase, FileText, Download, Clock, DollarSign } from 'lucide-react';
import { exportPrestacionesCSV, exportPrestacionesHTML } from '../../../utils/prestacionesExport';

export default function IndividualPrestacionesView({
  prestaciones,
  fechaSalidaPersonalizada,
  usarFechasPersonalizadas,
  horasExtrasPendientes,
  comisionesPendientes
}) {
  const handleExportCSV = () => exportPrestacionesCSV(prestaciones);

  const handleExportHTML = () => {
    const fechaSalida = usarFechasPersonalizadas && fechaSalidaPersonalizada
      ? fechaSalidaPersonalizada
      : null;
    exportPrestacionesHTML(prestaciones, fechaSalida);
  };

  const horasExtras = parseFloat(horasExtrasPendientes) || 0;
  const comisiones = parseFloat(comisionesPendientes) || 0;
  const totalConExtras = prestaciones.totalPrestaciones + horasExtras + comisiones;

  const fmt = (n) => parseFloat(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-4">

      {/* Total + Export */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Total a Pagar</p>
            <p className="text-3xl font-bold text-blue-700 mt-1">
              RD$ {fmt(totalConExtras)}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {prestaciones.anosServicio} año{prestaciones.anosServicio !== 1 ? 's' : ''} {prestaciones.mesesServicio} mes{prestaciones.mesesServicio !== 1 ? 'es' : ''} {prestaciones.diasServicio} día{prestaciones.diasServicio !== 1 ? 's' : ''} de servicio
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleExportHTML}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
            >
              <FileText className="w-3.5 h-3.5" />
              PDF
            </button>
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Excel
            </button>
          </div>
        </div>

        {/* Mini resumen de conceptos */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 pt-4 border-t border-gray-100">
          <div className="text-center">
            <p className="text-[10px] text-gray-400">Vacaciones</p>
            <p className="text-xs font-semibold text-gray-700 mt-0.5">RD$ {fmt(prestaciones.vacaciones.monto)}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-gray-400">Navidad</p>
            <p className="text-xs font-semibold text-gray-700 mt-0.5">RD$ {fmt(prestaciones.salarioNavidad.monto)}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-gray-400">Preaviso</p>
            <p className="text-xs font-semibold text-gray-700 mt-0.5">RD$ {fmt(prestaciones.preaviso.monto)}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] text-gray-400">Cesantía</p>
            <p className="text-xs font-semibold text-gray-700 mt-0.5">RD$ {fmt(prestaciones.cesantia.monto)}</p>
          </div>
        </div>
      </div>

      {/* Desglose detallado */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Desglose Detallado</h3>
        </div>
        <div className="divide-y divide-gray-100">

          {/* Vacaciones */}
          <div className="px-4 py-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className="bg-blue-50 p-1.5 rounded-lg">
                  <Calendar className="w-4 h-4 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Vacaciones</p>
                  <p className="text-xs text-gray-400">
                    {prestaciones.vacaciones.diasPago.toFixed(1)} días a pagar
                    {prestaciones.vacaciones.tomoVacaciones && ' · Escala proporcional'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-gray-900">RD$ {fmt(prestaciones.vacaciones.monto)}</p>
                <p className="text-[10px] text-gray-400">Art. 178</p>
              </div>
            </div>
            {prestaciones.vacaciones.tomoVacaciones && prestaciones.anos >= 1 && (
              <div className="mt-2 ml-9 text-xs text-gray-500 bg-gray-50 rounded px-2 py-1.5">
                Desde último aniversario: {Math.floor(prestaciones.vacaciones.mesesDesdeAniversario)} meses · {getEscalaText(prestaciones.vacaciones.mesesDesdeAniversario)}
              </div>
            )}
          </div>

          {/* Salario Navidad */}
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="bg-green-50 p-1.5 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Salario de Navidad</p>
                <p className="text-xs text-gray-400">
                  {prestaciones.salarioNavidad.proporcional ? 'Proporcional al tiempo' : 'Pago completo'}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-gray-900">RD$ {fmt(prestaciones.salarioNavidad.monto)}</p>
              <p className="text-[10px] text-gray-400">Ley 491-08</p>
            </div>
          </div>

          {/* Preaviso */}
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="bg-orange-50 p-1.5 rounded-lg">
                <AlertCircle className="w-4 h-4 text-orange-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Preaviso</p>
                <p className="text-xs text-gray-400">{prestaciones.preaviso.dias} días</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-bold text-gray-900">RD$ {fmt(prestaciones.preaviso.monto)}</p>
              <p className="text-[10px] text-gray-400">Art. 75-80</p>
            </div>
          </div>

          {/* Cesantía */}
          <div className="px-4 py-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className="bg-purple-50 p-1.5 rounded-lg">
                  <Briefcase className="w-4 h-4 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Cesantía</p>
                  <p className="text-xs text-gray-400">{prestaciones.cesantia.dias.toFixed(1)} días total</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-gray-900">RD$ {fmt(prestaciones.cesantia.monto)}</p>
                <p className="text-[10px] text-gray-400">Art. 82</p>
              </div>
            </div>
            <div className="mt-2 ml-9 text-xs text-gray-400">
              Por años: {prestaciones.cesantia.diasAnos} días · Por meses: {prestaciones.cesantia.diasMeses} días
            </div>
          </div>

          {/* Horas extras (si aplica) */}
          {horasExtras > 0 && (
            <div className="px-4 py-3 flex items-center justify-between bg-orange-50">
              <div className="flex items-center gap-2.5">
                <div className="bg-orange-100 p-1.5 rounded-lg">
                  <Clock className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Horas Extras Pendientes</p>
                  <p className="text-xs text-gray-500">Concepto adicional</p>
                </div>
              </div>
              <p className="text-sm font-bold text-gray-900">RD$ {fmt(horasExtras)}</p>
            </div>
          )}

          {/* Comisiones (si aplica) */}
          {comisiones > 0 && (
            <div className="px-4 py-3 flex items-center justify-between bg-indigo-50">
              <div className="flex items-center gap-2.5">
                <div className="bg-indigo-100 p-1.5 rounded-lg">
                  <DollarSign className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Comisiones Pendientes</p>
                  <p className="text-xs text-gray-500">Concepto adicional</p>
                </div>
              </div>
              <p className="text-sm font-bold text-gray-900">RD$ {fmt(comisiones)}</p>
            </div>
          )}

          {/* Total row */}
          <div className="px-4 py-3 bg-blue-50 flex items-center justify-between">
            <p className="text-sm font-bold text-blue-900">Total a Pagar</p>
            <p className="text-base font-bold text-blue-700">RD$ {fmt(totalConExtras)}</p>
          </div>
        </div>
      </div>

      {/* Datos salariales */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Salarios de Referencia</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-400">Salario Actual</p>
            <p className="text-sm font-semibold text-gray-900 mt-0.5">
              RD$ {fmt(prestaciones.salarioBaseVacaciones)}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">Base cálculo vacaciones</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-400">Salario Promedio</p>
            <p className="text-sm font-semibold text-gray-900 mt-0.5">
              RD$ {fmt(prestaciones.salarioPromedio)}
            </p>
            <p className="text-[10px] text-gray-400 mt-0.5">Últimos 12 meses (base preaviso/cesantía)</p>
          </div>
        </div>
      </div>

      {/* Marco legal — colapsible */}
      <details className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
        <summary className="px-4 py-3 cursor-pointer text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2 hover:bg-gray-100 transition-colors select-none">
          <FileText className="w-3.5 h-3.5" />
          Marco Legal
        </summary>
        <div className="px-4 pb-4 space-y-1.5 text-xs text-gray-600">
          <p>• <strong>Vacaciones:</strong> Art. 178 — 6–12 días (&lt;1 año según meses), 14 días (1–5 años), 18 días (&gt;5 años)</p>
          <p>• <strong>Salario de Navidad:</strong> Ley 491-08 — 1/12 del salario anual</p>
          <p>• <strong>Preaviso:</strong> Art. 75-80 — De 7 a 28 días según antigüedad</p>
          <p>• <strong>Cesantía:</strong> Art. 82 — De 21 a 23 días por año trabajado</p>
          <p className="pt-2 text-gray-400">Cálculos basados en últimos 12 salarios cotizables. Consulte con un abogado laboralista para cálculos oficiales.</p>
        </div>
      </details>
    </div>
  );
}

// Helper
function getEscalaText(meses) {
  if (meses < 5) return '0 días (menos de 5 meses)';
  if (meses < 6) return '6 días (5 meses)';
  if (meses < 7) return '7 días (6 meses)';
  if (meses < 8) return '8 días (7 meses)';
  if (meses < 9) return '9 días (8 meses)';
  if (meses < 10) return '10 días (9 meses)';
  if (meses < 11) return '11 días (10 meses)';
  if (meses < 12) return '12 días (11 meses)';
  return '14–18 días (12+ meses)';
}
